import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

import { PhoebeFlowerIcon } from '@apps/web/components/settings/phoebe_flower_icon';
import { MessageRatingButtons } from '@apps/web/components/shared/messages/message_rating_buttons';
import { MessageRatingTextarea } from '@apps/web/components/shared/messages/message_rating_textarea';
import { StagedContactCardPreview } from '@apps/web/components/shared/messages/staged_contact_card_preview';
import { Button } from '@apps/web/components/ui/button';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useMessageRating } from '@apps/web/hooks/use_message_rating';
import { useStagedMessageActions } from '@apps/web/hooks/use_staged_message_actions';
import { getApiClient } from '@apps/web/lib/api_client';
import { useStagedMessageRatings } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type StagedMessage } from '@libraries/typescript/api_client';

type MessagePendingProps = {
    readonly timezone: string;
    readonly stagedMessage: StagedMessage;
    readonly organizationId: string;
    readonly onUpdate: () => void;
};

export function MessagePending({
    timezone,
    stagedMessage,
    organizationId,
    onUpdate,
}: MessagePendingProps) {
    const [editedContent, setEditedContent] = useState(stagedMessage.content);
    const [showEnglish, setShowEnglish] = useState(false);
    const [loadedEnglishTranslation, setLoadedEnglishTranslation] = useState(
        stagedMessage.english_translation,
    );
    const englishTranslation =
        editedContent === stagedMessage.content
            ? (stagedMessage.english_translation ?? loadedEnglishTranslation)
            : null;
    const showingEnglish =
        showEnglish &&
        englishTranslation !== null &&
        englishTranslation !== undefined;

    useEffect(() => {
        setLoadedEnglishTranslation(stagedMessage.english_translation);
        if (
            stagedMessage.english_translation_attempted ||
            (stagedMessage.english_translation !== null &&
                stagedMessage.english_translation !== undefined)
        ) {
            return;
        }

        let isCurrent = true;
        void getApiClient()
            .post({
                url: '/organizations/{organization_id}/pending-messages/{pending_message_id}/english-translation',
                pathParams: {
                    organization_id: organizationId,
                    pending_message_id: stagedMessage.id,
                },
            })
            .then((response) => {
                if (isCurrent) {
                    setLoadedEnglishTranslation(response.english_translation);
                }
            })
            .catch((error: unknown) => {
                console.error(
                    'Failed to translate pending message to English:',
                    error,
                );
            });

        return () => {
            isCurrent = false;
        };
    }, [
        organizationId,
        stagedMessage.english_translation,
        stagedMessage.english_translation_attempted,
        stagedMessage.id,
    ]);

    const { data: ratingResponses, mutate: mutateAll } =
        useStagedMessageRatings(organizationId, {
            reason: 'message-pending',
        });

    const rating =
        ratingResponses?.ratings.find(
            (rating) => rating.staged_message_id === stagedMessage.id,
        ) ?? null;

    const {
        selectedLabel,
        isSaving,
        draftComment,
        setDraftComment,
        hasUnsavedComment,
        isOperationPending,
        handleLabelClick,
        handleCommentChange,
        handleSaveComment,
    } = useMessageRating({
        rating,
        mutateAllRatings: mutateAll,
        messageId: null,
        organizationId,
        stagedMessageId: stagedMessage.id,
        isAdmin: false,
    });

    const { handleApprove, handleReject, isApproving, isRejecting } =
        useStagedMessageActions({
            organizationId,
            onUpdate,
        });
    const approving = isApproving(stagedMessage.id);
    const rejecting = isRejecting(stagedMessage.id);
    const actionsDisabled =
        approving || rejecting || isSaving || isOperationPending;

    return (
        <div className="flex justify-end">
            <div className="w-full space-y-2 sm:max-w-[85%]">
                <section
                    aria-label="Phoebe reply pending review"
                    className="space-y-1.5"
                >
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-1">
                        <div className="flex min-w-0 items-center gap-1.5">
                            <PhoebeFlowerIcon className="text-agent-action size-4 shrink-0" />
                            <h3 className="min-w-0 text-xs leading-5 font-medium">
                                Phoebe reply
                                <span className="text-muted-foreground font-normal">
                                    {' '}
                                    — review before sending
                                </span>
                            </h3>
                        </div>
                        <time
                            dateTime={stagedMessage.created_at}
                            className="text-muted-foreground shrink-0 text-xs"
                        >
                            {new Date(stagedMessage.created_at).toLocaleString(
                                'en-US',
                                {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                    timeZone: timezone,
                                },
                            )}
                        </time>
                    </div>
                    <div className="border-agent-action/25 bg-agent-action/5 dark:border-agent-action/40 dark:bg-agent-action/10 rounded-lg border p-2">
                        {showingEnglish ? (
                            <p
                                // Keyed by displayed language so the swap
                                // remounts the text and replays the fade,
                                // matching the sent message bubbles.
                                key="english"
                                className="animate-in fade-in text-foreground min-h-16 w-full px-2 py-1.5 text-sm leading-5 whitespace-pre-wrap duration-150"
                            >
                                {englishTranslation}
                            </p>
                        ) : (
                            <div
                                key="original"
                                className="animate-in fade-in duration-150"
                            >
                                <Textarea
                                    aria-label="Reply text"
                                    value={editedContent}
                                    onChange={(e) => {
                                        setEditedContent(e.target.value);
                                    }}
                                    disabled={approving || rejecting}
                                    className="border-agent-action/20 bg-background/80 focus-visible:!border-agent-action focus-visible:!ring-agent-action/30 min-h-16 resize-y text-sm leading-5 shadow-none focus-visible:!ring-2"
                                />
                            </div>
                        )}
                        {englishTranslation !== null &&
                            englishTranslation !== undefined && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEnglish(!showingEnglish);
                                    }}
                                    className="text-agent-action hover:text-agent-action-strong focus-visible:ring-agent-action/30 mt-1 min-h-7 rounded-sm px-1 text-xs font-medium underline underline-offset-2 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                                >
                                    {showingEnglish
                                        ? 'View original message'
                                        : 'View in English'}
                                </button>
                            )}
                    </div>
                    {stagedMessage.attachment_type === 'contact_card' &&
                        stagedMessage.contact_card && (
                            <StagedContactCardPreview
                                displayName={
                                    stagedMessage.contact_card.display_name
                                }
                                phoneE164={
                                    stagedMessage.contact_card.phone_e164
                                }
                            />
                        )}
                    <div className="flex flex-col gap-2 px-1 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-1">
                            <span className="text-muted-foreground text-xs">
                                Rate this reply
                            </span>
                            <MessageRatingButtons
                                selectedLabel={selectedLabel}
                                isSaving={isSaving}
                                isOperationPending={isOperationPending}
                                onLabelClick={handleLabelClick}
                                disabled={approving || rejecting}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                status={rejecting ? 'pending' : 'idle'}
                                onClick={() => handleReject(stagedMessage)}
                                disabled={actionsDisabled}
                                className="h-7 px-2 text-xs"
                            >
                                Reject
                            </Button>
                            <Button
                                size="sm"
                                status={approving ? 'pending' : 'idle'}
                                onClick={() =>
                                    handleApprove(stagedMessage, editedContent)
                                }
                                disabled={actionsDisabled}
                                className="border-agent-action bg-agent-action hover:bg-agent-action-strong focus-visible:ring-agent-action/35 h-7 px-2.5 text-xs text-white"
                            >
                                {!approving && (
                                    <Check
                                        aria-hidden="true"
                                        className="size-4"
                                    />
                                )}
                                Send
                            </Button>
                        </div>
                    </div>
                </section>
                <MessageRatingTextarea
                    selectedLabel={selectedLabel}
                    isSaving={isSaving}
                    isOperationPending={isOperationPending}
                    draftComment={draftComment}
                    hasUnsavedComment={hasUnsavedComment}
                    onCommentChange={handleCommentChange}
                    onSaveComment={handleSaveComment}
                    disabled={approving || rejecting}
                    organizationId={organizationId}
                    onSetDraftComment={setDraftComment}
                />
            </div>
        </div>
    );
}
